import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Clock, BarChart3, Smartphone, Store, Ban } from 'lucide-react';
import { staggerContainer, fadeInUp } from '@/hooks/use-scroll-animation';
import { Button } from '@/components/ui/button';

// Hero Image
import cafeDashboard from '@/assets/decorative/business/cafeteria-balcao.png';

// Showcase Images (5 steps)
import imgCadastro from '@/assets/decorative/business/01-cadastro-premium.jpg';
import imgPerfil from '@/assets/decorative/business/02-personaliza-perfil.jpg';
import imgFidelidade from '@/assets/decorative/business/03-regras-fidelidade.jpg';
import imgCoffeeGuide from '@/assets/decorative/business/04-coffee-guide.jpg';
import imgDashboard from '@/assets/decorative/business/05-dashboard.jpg';

const showcaseItems = [
    { key: 'cadastro', img: imgCadastro, alt: 'Cadastro Premium' },
    { key: 'perfil', img: imgPerfil, alt: 'Perfil da Cafeteria' },
    { key: 'fidelidade', img: imgFidelidade, alt: 'Regras de Fidelidade' },
    { key: 'coffeeGuide', img: imgCoffeeGuide, alt: 'Coffee Guide' },
    { key: 'dashboard', img: imgDashboard, alt: 'Dashboard de Resultados' },
];

export function BusinessSection() {
    const { t } = useTranslation();

    const socialProofItems = [
        { key: 'setup', icon: Clock },
        { key: 'realtime', icon: BarChart3 },
        { key: 'noPaper', icon: Smartphone },
        { key: 'security', icon: Ban },
    ];

    return (
        <section
            id="business-section"
            className="py-20 md:py-28 relative overflow-hidden"
            style={{
                background: 'linear-gradient(180deg, #F5EBE0 0%, #EDE5DB 100%)'
            }}
        >
            <div className="container mx-auto px-4">

                {/* ── Main Layout ──
                    Mobile:  Header → Image → Steps → CTA  (flex column)
                    Desktop: [Header + Steps + CTA | Image] (2-col grid)
                */}
                <div className="flex flex-col xl:grid xl:grid-cols-2 gap-10 xl:gap-16 xl:items-center">

                    {/* ① Header: Badge + Headline + Subtitle — always first */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={staggerContainer}
                        className="order-1 xl:col-start-1 xl:row-start-1"
                    >
                        {/* Badge — Padronizado com Consumer Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4E37]/10 text-[#6F4E37] text-sm font-medium mb-6"
                        >
                            <Store size={14} />
                            <span className="uppercase tracking-wider text-xs font-bold">
                                {t('business.badge')}
                            </span>
                        </motion.div>

                        {/* Headline */}
                        <motion.h2
                            variants={fadeInUp}
                            className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-[#2D1810] mb-4 leading-tight"
                        >
                            {t('business.title')}
                        </motion.h2>

                        {/* Subheadline */}
                        <motion.p
                            variants={fadeInUp}
                            className="text-lg md:text-xl text-[#6B5B4F]"
                        >
                            {t('business.subtitle')}
                        </motion.p>
                    </motion.div>

                    {/* ② Hero Image — second on mobile, right column on desktop */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="order-2 xl:col-start-2 xl:row-start-1 xl:row-span-2 flex justify-center items-center w-full"
                    >
                        <div className="relative w-full max-w-[500px] rounded-[32px] overflow-hidden shadow-xl group">
                            <img
                                src={cafeDashboard}
                                alt="Coffee shop dashboard"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                    </motion.div>

                    {/* ③ Steps + CTA — third on mobile, below header on desktop */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                        className="order-3 xl:col-start-1 xl:row-start-2"
                    >
                        {/* Steps with Connecting Line */}
                        <div className="relative pl-8 mb-10">
                            {/* Vertical Connecting Line */}
                            <div
                                className="absolute left-3 top-3 bottom-0 w-0.5 pointer-events-none"
                                style={{
                                    top: '1rem',
                                    height: 'calc(100% - 2rem)',
                                    background: 'linear-gradient(180deg, #C4A77D 0%, #8B7355 100%)'
                                }}
                            />

                            <div className="space-y-8 relative z-10">
                                {[1, 2, 3, 4, 5].map((step) => (
                                    <motion.div
                                        key={step}
                                        variants={fadeInUp}
                                        className="flex gap-4 relative items-start"
                                    >
                                        {/* Step Circle */}
                                        <div className="absolute -left-[2.1rem] w-7 h-7 rounded-full bg-[#FDF8F3] border-2 border-[#C4A77D] flex items-center justify-center text-xs font-bold text-[#6F4E37] shadow-sm z-20">
                                            {step}
                                        </div>

                                        <div className="pt-0.5">
                                            <h3 className="font-bold text-lg text-[#2D1810] mb-1">
                                                {t(`business.steps.${step}.title`)}
                                            </h3>
                                            <p className="text-[#6B5B4F]">
                                                {t(`business.steps.${step}.desc`)}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* CTA Button — centered on mobile, left-aligned on desktop */}
                        <motion.div variants={fadeInUp} className="text-center xl:text-left">
                            <Button
                                size="lg"
                                className="rounded-full px-8 bg-[#6F4E37] hover:bg-[#5D3D2E] text-white shadow-lg shadow-[#6F4E37]/20 transition-all hover:scale-105"
                            >
                                {t('business.cta')}
                            </Button>
                        </motion.div>
                    </motion.div>
                </div>

                {/* ──────────── Showcase: Recursos da plataforma ──────────── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={staggerContainer}
                    className="mt-20 md:mt-28"
                >
                    {/* Showcase Header — Fix: removed max-w constraint that broke mobile */}
                    <motion.div variants={fadeInUp} className="text-center mb-12">
                        <h3 className="text-2xl md:text-4xl font-display font-bold text-[#2D1810] mb-3">
                            {t('business.showcase.title')}
                        </h3>
                        <p className="text-[#6B5B4F] text-base md:text-lg leading-relaxed px-4 md:px-0">
                            {t('business.showcase.subtitle')}
                        </p>
                    </motion.div>

                    {/* Desktop Grid: 3 + 2 assimétrico */}
                    <div className="hidden xl:block">
                        {/* Row 1: 3 cards iguais */}
                        <div className="grid grid-cols-3 gap-6 mb-6">
                            {showcaseItems.slice(0, 3).map(({ key, img, alt }, i) => (
                                <motion.div
                                    key={key}
                                    variants={fadeInUp}
                                    transition={{ delay: i * 0.1 }}
                                    className="rounded-[32px] overflow-hidden shadow-lg relative group aspect-[4/3] bg-[#FDF8F3]"
                                >
                                    <img
                                        src={img}
                                        alt={alt}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    {/* Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-[#2D1810]/80 to-transparent pt-16">
                                        <h4 className="text-white font-display font-bold text-lg">
                                            {t(`business.showcase.items.${key}.title`)}
                                        </h4>
                                        <p className="text-white/75 text-sm mt-0.5">
                                            {t(`business.showcase.items.${key}.desc`)}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Row 2: 2 cards maiores */}
                        <div className="grid grid-cols-2 gap-6">
                            {showcaseItems.slice(3, 5).map(({ key, img, alt }, i) => (
                                <motion.div
                                    key={key}
                                    variants={fadeInUp}
                                    transition={{ delay: 0.3 + i * 0.1 }}
                                    className="rounded-[32px] overflow-hidden shadow-lg relative group aspect-[16/9] bg-[#FDF8F3]"
                                >
                                    <img
                                        src={img}
                                        alt={alt}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    {/* Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#2D1810]/80 to-transparent pt-20">
                                        <h4 className="text-white font-display font-bold text-xl">
                                            {t(`business.showcase.items.${key}.title`)}
                                        </h4>
                                        <p className="text-white/75 text-sm mt-1">
                                            {t(`business.showcase.items.${key}.desc`)}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile Grid: 2 columns */}
                    <div className="xl:hidden grid grid-cols-2 gap-4 md:gap-6">
                        {showcaseItems.map(({ key, img, alt }, i) => (
                            <motion.div
                                key={key}
                                variants={fadeInUp}
                                transition={{ delay: i * 0.08 }}
                                className={`rounded-[24px] overflow-hidden shadow-md relative group bg-[#FDF8F3] ${i === 4 ? 'col-span-2 aspect-[16/9]' : 'aspect-square'
                                    }`}
                            >
                                <img
                                    src={img}
                                    alt={alt}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-[#2D1810]/80 to-transparent pt-10">
                                    <h4 className="text-white font-display font-bold text-sm leading-tight">
                                        {t(`business.showcase.items.${key}.title`)}
                                    </h4>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>



                {/* Market Stat */}
                <p className="text-center mt-10 text-sm text-[#6B5B4F] italic opacity-80">
                    "{t('business.marketStat.text')}" — {t('business.marketStat.source')}
                </p>

                {/* ──────────── Social Proof Bar ──────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="mt-16 pt-10 border-t border-[#D7CCC8]"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {socialProofItems.map(({ key, icon: Icon }) => (
                            <div key={key} className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#6F4E37]/10 flex items-center justify-center flex-shrink-0">
                                    <Icon size={20} className="text-[#6F4E37]" />
                                </div>
                                <span className="text-sm font-medium text-[#2D1810]">
                                    {t(`business.socialProof.${key}`)}
                                </span>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
